import { useMemo } from 'react';
import { Address, erc20Abi, formatUnits } from 'viem';
import { useConnection, useReadContracts } from 'wagmi';

import { useVaults } from '@/app/components/VaultsPageComponents/hooks/useVaults';
import { MZUSD_DECIMALS } from '@/app/shared/constants/common';
import { Interval } from '@/app/shared/constants/interval';
import { TOKENS_ADDRESS_ARR } from '@/app/shared/constants/tokens';
import { getChainMzusdTokens } from '@/app/shared/constants/web3/chainContracts';
import { ABI } from '@/app/shared/constants/web3/constants';
import { useBalances } from '@/app/shared/hooks/blockchain/useBalances';
import {
  SequentialStepPlan,
  useSendSequentialSteps,
} from '@/app/shared/hooks/blockchain/useSendSequentialSteps/useSendSequentialSteps';
import { formatTokenAmount, shortenAddress } from '@/app/shared/lib/formatters';

export type RedeemableMzusd = {
  vaultAddress: Address;
  vaultName: string;
  mzusd: Address;
  balance: bigint;
  balanceFormatted: string;
  /** Contract rejects smaller amounts (TooSmallWithdrawalAmount). */
  canRedeemOnChain: boolean;
};

/**
 * MZUSD is 18 dec, USDC is 6. Below ~1e12 wei requestWithdrawal yields usdcOut=0
 * and reverts with TooSmallWithdrawalAmount.
 */
export const MIN_REDEEMABLE_MZUSD = 10n ** 12n;

/** Per-vault MZUSD balances (claim interrupted / external transfers). */
export const useRedeemableMzusdBalances = () => {
  const { address, chainId, isConnected } = useConnection();
  const tokens = useMemo(() => getChainMzusdTokens(chainId), [chainId]);
  const { vaults } = useVaults('current');

  const vaultNameById = useMemo(() => {
    const map = new Map<string, string>();
    vaults.forEach((vault) => {
      map.set(vault.id.toLowerCase(), vault.name);
    });
    return map;
  }, [vaults]);

  const { data, isLoading, refetch } = useReadContracts({
    contracts: tokens.map((token) => ({
      address: token.mzusd,
      abi: erc20Abi,
      functionName: 'balanceOf' as const,
      args: address ? ([address] as const) : undefined,
    })),
    query: {
      enabled: isConnected && !!address && tokens.length > 0,
      staleTime: 0,
      refetchOnMount: 'always',
      refetchInterval: Interval.THIRTY_SEC,
    },
  });

  const redeemable = useMemo((): RedeemableMzusd[] => {
    if (!address || !data) return [];

    return tokens.flatMap((token, index) => {
      const result = data[index]?.result;
      // Only show balances the contract will actually accept.
      if (typeof result !== 'bigint' || result < MIN_REDEEMABLE_MZUSD) return [];

      const vaultKey = token.vaultAddress.toLowerCase();
      const vaultName =
        vaultNameById.get(vaultKey) ?? `Vault ${shortenAddress(token.vaultAddress)}`;

      return [
        {
          vaultAddress: token.vaultAddress,
          vaultName,
          mzusd: token.mzusd,
          balance: result,
          balanceFormatted: formatTokenAmount(result.toString(), MZUSD_DECIMALS),
          canRedeemOnChain: true,
        },
      ];
    });
  }, [address, data, tokens, vaultNameById]);

  return {
    redeemable,
    isLoading,
    refetch,
    isConnected,
  };
};

interface UseRedeemMzusdArgs {
  item: RedeemableMzusd | null;
  enabled?: boolean;
}

/** Redeem one vault MZUSD balance → USDC. */
export const useRedeemMzusd = ({ item, enabled = true }: UseRedeemMzusdArgs) => {
  const { address, isConnected } = useConnection();
  const { refetch: refetchWalletBalances } = useBalances({
    tokens: TOKENS_ADDRESS_ARR,
    address,
    enabled: false,
  });

  const plannedSteps = useMemo((): SequentialStepPlan[] => {
    if (!enabled || !address || !item || !item.canRedeemOnChain) return [];

    return [
      {
        id: `redeem-mzusd-${item.mzusd}`,
        title: 'Redeem MZUSD',
        description: `Redeem ${item.balanceFormatted} MZUSD from ${item.vaultName} to receive USDC`,
        isComplete: false,
        tx: {
          address: item.mzusd,
          abi: ABI.MZUSD,
          functionName: 'requestWithdrawal',
          args: [item.balance],
        },
      },
    ];
  }, [address, enabled, item]);

  const sequential = useSendSequentialSteps({
    steps: plannedSteps,
    planKey: `${item?.mzusd ?? ''}:${item?.balance.toString() ?? ''}:${address ?? ''}`,
    enabled: enabled && isConnected && plannedSteps.length > 0,
  });

  const amountFormatted =
    item && item.balance > 0n ? formatUnits(item.balance, MZUSD_DECIMALS) : '0';

  return {
    ...sequential,
    isReady: enabled && isConnected && !!address && !!item?.canRedeemOnChain && plannedSteps.length > 0,
    amountFormatted,
    refetchWalletBalances,
    isBatchingTransaction: false as const,
  };
};
